export const AUTH_TAB_ID_STORAGE_KEY = "grain-auth-tab-id";
export const SUPABASE_AUTH_STORAGE_KEY_PREFIX = "grain-supabase-auth";
export const LOCAL_AUTH_BYPASS_SESSION_STATE_STORAGE_KEY = "grain-local-auth-bypass-session-state";
export const LINKED_TAB_AUTH_BOOTSTRAP_STORAGE_KEY = "grain-linked-tab-auth-bootstrap";

const AUTH_STORAGE_PROBE_KEY_PREFIX = "__grain-auth-session-storage-probe__";
const TAB_ID_PATTERN = /^[a-z0-9_-]{1,128}$/i;
const LINKED_TAB_AUTH_BOOTSTRAP_TTL_MS = 30_000;
const fallbackContexts = new WeakMap();
let serverFallbackContext = null;

function getDefaultWindow() {
  return typeof window === "undefined" ? undefined : window;
}

function normalizeTabId(value) {
  const normalizedValue = typeof value === "string" ? value.trim() : "";
  return TAB_ID_PATTERN.test(normalizedValue) ? normalizedValue : "";
}

export function createRandomAuthTabId() {
  const cryptoApi = typeof globalThis !== "undefined" ? globalThis.crypto : null;
  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }

  if (typeof cryptoApi?.getRandomValues === "function") {
    const values = new Uint8Array(16);
    cryptoApi.getRandomValues(values);
    return Array.from(values, (value) => value.toString(16).padStart(2, "0")).join("");
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

export function createInMemoryAuthStorage() {
  const values = new Map();

  return {
    getItem(key) {
      return values.has(key) ? values.get(key) : null;
    },
    removeItem(key) {
      values.delete(key);
    },
    setItem(key, value) {
      values.set(key, String(value));
    },
  };
}

function createFallbackContext() {
  return {
    storage: createInMemoryAuthStorage(),
    tabId: "",
  };
}

function getFallbackContext(targetWindow) {
  if (targetWindow && (typeof targetWindow === "object" || typeof targetWindow === "function")) {
    const existingContext = fallbackContexts.get(targetWindow);
    if (existingContext) {
      return existingContext;
    }

    const nextContext = createFallbackContext();
    fallbackContexts.set(targetWindow, nextContext);
    return nextContext;
  }

  if (!serverFallbackContext) {
    serverFallbackContext = createFallbackContext();
  }

  return serverFallbackContext;
}

function getWorkingSessionStorage(targetWindow) {
  let storage = null;
  try {
    storage = targetWindow?.sessionStorage ?? null;
  } catch {
    return null;
  }

  if (!storage
    || typeof storage.getItem !== "function"
    || typeof storage.setItem !== "function"
    || typeof storage.removeItem !== "function") {
    return null;
  }

  const probeKey = `${AUTH_STORAGE_PROBE_KEY_PREFIX}-${createRandomAuthTabId()}`;
  try {
    storage.setItem(probeKey, "1");
    const isWritable = storage.getItem(probeKey) === "1";
    storage.removeItem(probeKey);
    return isWritable ? storage : null;
  } catch {
    try {
      storage.removeItem(probeKey);
    } catch {}
    return null;
  }
}

function readStorageItem(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorageItem(storage, key, value) {
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function removeStorageItem(storage, key) {
  try {
    storage.removeItem(key);
  } catch {}
}

function getWorkingLocalStorage(targetWindow = getDefaultWindow()) {
  try {
    const storage = targetWindow?.localStorage ?? null;
    if (!storage
      || typeof storage.getItem !== "function"
      || typeof storage.setItem !== "function"
      || typeof storage.removeItem !== "function") {
      return null;
    }
    return storage;
  } catch {
    return null;
  }
}

function collectLinkedTabAuthBootstrapEntries(sessionStorage, tabId) {
  const storageKey = getSupabaseAuthStorageKey(tabId);
  const entries = {};

  for (const key of [
    AUTH_TAB_ID_STORAGE_KEY,
    storageKey,
    `${storageKey}-code-verifier`,
    `${storageKey}-user`,
    LOCAL_AUTH_BYPASS_SESSION_STATE_STORAGE_KEY,
  ]) {
    const value = readStorageItem(sessionStorage, key);
    if (value !== null) {
      entries[key] = value;
    }
  }

  return entries;
}

export function publishLinkedTabAuthBootstrap(targetWindow = getDefaultWindow()) {
  const sessionStorage = getWorkingSessionStorage(targetWindow);
  const localStorage = getWorkingLocalStorage(targetWindow);
  if (!sessionStorage || !localStorage) {
    return false;
  }

  const tabId = normalizeTabId(readStorageItem(sessionStorage, AUTH_TAB_ID_STORAGE_KEY));
  if (!tabId) {
    return false;
  }

  const entries = collectLinkedTabAuthBootstrapEntries(sessionStorage, tabId);
  if (!entries[AUTH_TAB_ID_STORAGE_KEY]) {
    return false;
  }

  try {
    localStorage.setItem(LINKED_TAB_AUTH_BOOTSTRAP_STORAGE_KEY, JSON.stringify({
      createdAt: Date.now(),
      entries,
    }));
    return true;
  } catch {
    return false;
  }
}

function consumeLinkedTabAuthBootstrap(sessionStorage, localStorage) {
  let rawBootstrap = null;
  try {
    rawBootstrap = localStorage.getItem(LINKED_TAB_AUTH_BOOTSTRAP_STORAGE_KEY);
  } catch {
    return false;
  }

  if (!rawBootstrap) {
    return false;
  }

  try {
    localStorage.removeItem(LINKED_TAB_AUTH_BOOTSTRAP_STORAGE_KEY);
  } catch {}

  let bootstrap = null;
  try {
    bootstrap = JSON.parse(rawBootstrap);
  } catch {
    return false;
  }

  if (!bootstrap || typeof bootstrap !== "object") {
    return false;
  }

  const createdAt = Number(bootstrap.createdAt);
  if (!Number.isFinite(createdAt) || Date.now() - createdAt > LINKED_TAB_AUTH_BOOTSTRAP_TTL_MS) {
    return false;
  }

  const entries = bootstrap.entries;
  if (!entries || typeof entries !== "object") {
    return false;
  }

  let didRestore = false;
  for (const [key, value] of Object.entries(entries)) {
    if (typeof value === "string" && writeStorageItem(sessionStorage, key, value)) {
      didRestore = true;
    }
  }

  return didRestore;
}

export function getSupabaseAuthStorageKey(tabId) {
  return `${SUPABASE_AUTH_STORAGE_KEY_PREFIX}-${tabId}`;
}

export function clearSupabaseAuthStorageNamespace(storage, tabId) {
  const storageKey = getSupabaseAuthStorageKey(tabId);
  removeStorageItem(storage, storageKey);
  removeStorageItem(storage, `${storageKey}-code-verifier`);
  removeStorageItem(storage, `${storageKey}-user`);
}

export function isSwitchAccountRequested(targetWindow = getDefaultWindow()) {
  const search = typeof targetWindow?.location?.search === "string"
    ? targetWindow.location.search
    : "";
  if (!search) {
    return false;
  }

  try {
    return new URLSearchParams(search).get("switchAccount") === "1";
  } catch {
    return false;
  }
}

function getPersistentTabId(storage, createTabId) {
  const existingTabId = normalizeTabId(readStorageItem(storage, AUTH_TAB_ID_STORAGE_KEY));
  if (existingTabId) {
    return existingTabId;
  }

  const nextTabId = createTabId();
  return writeStorageItem(storage, AUTH_TAB_ID_STORAGE_KEY, nextTabId) ? nextTabId : "";
}

function getInMemoryTabId(context, createTabId) {
  const existingTabId = normalizeTabId(context.tabId);
  if (existingTabId) {
    return existingTabId;
  }

  const nextTabId = createTabId();
  context.tabId = nextTabId;
  return nextTabId;
}

function createAuthStorageConfiguration({
  storage,
  tabId,
  persistSession,
}) {
  return {
    persistSession,
    storage,
    storageKey: getSupabaseAuthStorageKey(tabId),
    tabId,
  };
}

/**
 * Resolves the auth namespace for the current browser tab.
 *
 * A `switchAccount=1` navigation is handled before Supabase is created. That
 * is important for duplicated tabs: browsers clone sessionStorage for a new
 * tab, so the inherited namespace must be removed and rotated before a client
 * can restore it.
 */
export function createTabScopedAuthStorage({
  createTabId = createRandomAuthTabId,
  switchAccount = undefined,
  targetWindow = getDefaultWindow(),
} = {}) {
  const shouldSwitchAccount = switchAccount === undefined
    ? isSwitchAccountRequested(targetWindow)
    : Boolean(switchAccount);
  const sessionStorage = getWorkingSessionStorage(targetWindow);

  if (!sessionStorage) {
    const fallbackContext = getFallbackContext(targetWindow);
    if (shouldSwitchAccount) {
      const previousTabId = normalizeTabId(fallbackContext.tabId);
      if (previousTabId) {
        clearSupabaseAuthStorageNamespace(fallbackContext.storage, previousTabId);
      }
      removeStorageItem(fallbackContext.storage, LOCAL_AUTH_BYPASS_SESSION_STATE_STORAGE_KEY);
      fallbackContext.tabId = "";
    }
    const tabId = getInMemoryTabId(fallbackContext, createTabId);
    return createAuthStorageConfiguration({
      persistSession: false,
      storage: fallbackContext.storage,
      tabId,
    });
  }

  if (!shouldSwitchAccount) {
    const localStorage = getWorkingLocalStorage(targetWindow);
    if (localStorage) {
      consumeLinkedTabAuthBootstrap(sessionStorage, localStorage);
    }
  }

  const previousTabId = getPersistentTabId(sessionStorage, createTabId);
  if (!previousTabId) {
    const fallbackContext = getFallbackContext(targetWindow);
    const tabId = getInMemoryTabId(fallbackContext, createTabId);
    return createAuthStorageConfiguration({
      persistSession: false,
      storage: fallbackContext.storage,
      tabId,
    });
  }

  if (shouldSwitchAccount) {
    clearSupabaseAuthStorageNamespace(sessionStorage, previousTabId);
    removeStorageItem(sessionStorage, LOCAL_AUTH_BYPASS_SESSION_STATE_STORAGE_KEY);

    const nextTabId = createTabId();
    if (writeStorageItem(sessionStorage, AUTH_TAB_ID_STORAGE_KEY, nextTabId)) {
      return createAuthStorageConfiguration({
        persistSession: true,
        storage: sessionStorage,
        tabId: nextTabId,
      });
    }

    const fallbackContext = getFallbackContext(targetWindow);
    const previousFallbackTabId = normalizeTabId(fallbackContext.tabId);
    if (previousFallbackTabId) {
      clearSupabaseAuthStorageNamespace(fallbackContext.storage, previousFallbackTabId);
    }
    removeStorageItem(fallbackContext.storage, LOCAL_AUTH_BYPASS_SESSION_STATE_STORAGE_KEY);
    fallbackContext.tabId = "";
    const tabId = getInMemoryTabId(fallbackContext, createTabId);
    return createAuthStorageConfiguration({
      persistSession: false,
      storage: fallbackContext.storage,
      tabId,
    });
  }

  return createAuthStorageConfiguration({
    persistSession: true,
    storage: sessionStorage,
    tabId: previousTabId,
  });
}
